import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/referral-terms")({
  component: ReferralTermsPage,
  head: () => ({
    meta: [
      { title: "Referral Program Terms — Inquira" },
      {
        name: "description",
        content: "Eligibility, qualifying referrals, free-month rewards, reversals and disclosure rules for the Inquira referral program.",
      },
      { property: "og:title", content: "Inquira Referral Program Terms" },
      { property: "og:description", content: "How Inquira referral free months are earned, verified, redeemed and reversed." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/referral-terms" }],
  }),
});

const SECTIONS: { h: string; p: string[] }[] = [
  {
    h: "1. Eligibility",
    p: [
      "The Inquira Referral Program is open to Inquira account holders in good standing who are of legal age in their jurisdiction. Suspended, restricted or fraudulent accounts are not eligible.",
      "Each participant receives one personal referral code. Additional codes are not issued per person referred.",
    ],
  },
  {
    h: "2. Qualifying referrals",
    p: [
      "A referral qualifies only when a new customer signs up using your personal referral link, creates a new Inquira account, becomes a qualifying paid Inquira Premium subscriber, and that paid subscription is verified by Inquira's billing systems (Stripe, Apple or Google).",
      "Clicking a referral link, creating an account, or starting a free trial does not by itself qualify a referral.",
    ],
  },
  {
    h: "3. Reward structure",
    p: [
      "Each verified qualifying referral earns one free month of Inquira Premium. Rewards accumulate: two qualifying referrals earn two free months, five earn five, and so on.",
      "Free months are recorded in your Inquira account and applied as Premium access time when redeemed. Free months have no cash value and cannot be transferred, sold or exchanged.",
    ],
  },
  {
    h: "4. Self-referrals, duplicates and fraud",
    p: [
      "Self-referrals do not qualify. Duplicate accounts, automated signups, purchased traffic, misleading claims and any other abuse of the program do not qualify and may result in removal from the program.",
      "Inquira may flag suspicious activity for manual review before approving a reward. Households sharing a network are not disqualified for that reason alone.",
    ],
  },
  {
    h: "5. Refunds, chargebacks and reversals",
    p: [
      "If the referred customer's qualifying transaction is refunded, reversed, charged back or determined to be fraudulent, the related reward may be marked reversed if it has not yet been redeemed.",
      "Reward status is determined solely by Inquira's backend using verified billing information. Reward status cannot be changed from the app.",
    ],
  },
  {
    h: "6. Subscription and billing restrictions",
    p: [
      "Free months are applied by Inquira and do not modify or override any subscription managed by Apple or Google. Where your subscription is billed by Apple or Google, that subscription continues to renew under the store's terms until you cancel it with the store.",
      "Redeeming a free month does not cancel an active store subscription, and Inquira does not alter store-managed billing or expiration dates.",
    ],
  },
  {
    h: "7. TikTok and disclosure requirements",
    p: [
      "TikTok does not sponsor, endorse, administer or otherwise participate in the Inquira Referral Program.",
      "If you receive rewards for promoting Inquira, you must clearly disclose your relationship with Inquira in accordance with applicable advertising laws and the rules of the platform you post on.",
    ],
  },
  {
    h: "8. Program changes",
    p: [
      "Inquira may modify, suspend or terminate the Referral Program, or change reward amounts and eligibility requirements, at any time. Rewards already granted are not affected by changes that take effect afterwards.",
      "These terms supplement, and do not replace, the Inquira Terms of Service and Privacy Policy.",
    ],
  },
];

function ReferralTermsPage() {
  return (
    <div className="min-h-screen notebook-lines px-4 pb-16">
      <div className="max-w-2xl mx-auto pt-6 space-y-6">
        <Link to="/referrals" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> Back to referrals
        </Link>

        <header className="space-y-2">
          <h1 className="font-display font-black text-3xl text-ink">Referral Program Terms</h1>
          <p className="text-sm text-ink-muted">
            These terms govern the Inquira Referral Program, including how free months are earned, verified and reversed.
          </p>
        </header>

        <div className="space-y-5">
          {SECTIONS.map((s) => (
            <section key={s.h} className="bg-card rounded-2xl p-6 border border-rule space-y-2">
              <h2 className="font-display font-bold text-ink text-base">{s.h}</h2>
              {s.p.map((para) => (
                <p key={para} className="text-sm text-ink-muted leading-relaxed">
                  {para}
                </p>
              ))}
            </section>
          ))}
        </div>

        <p className="text-xs text-ink-muted text-center">
          Questions? Contact <a className="underline" href="mailto:support@inquira.online">support@inquira.online</a>.
        </p>
      </div>
    </div>
  );
}
